// R8 graduation: record a local ladder win, and — once a graduated match has
// booted — drip one of its unannounced modules as a "new rule" cinematic (the
// count lives in localStorage, so each match of a fresh preset announces
// exactly one more). Extracted from App.tsx (ROADMAP 7.14); the hook order is
// unchanged.

import type { MatchSetup } from '@aod/protocol';
import { useEffect, useRef } from 'react';
import type { useCinematicDirector } from '../cinematics/useCinematicDirector.js';
import { useT } from '../i18n/index.js';
import { isLadderId, markAnnounced, markMastered, newRulePlan } from '../lib/graduation.js';
import { moduleLabel } from '../setup/presentation.js';
import type { SimWorld } from '../sim/useSimWorld/index.js';

export function useGraduation(
  sim: SimWorld,
  matchSetup: MatchSetup | null,
  cinematics: ReturnType<typeof useCinematicDirector>,
  localPlayerIndex: number,
) {
  const t = useT();
  const announcedFor = useRef<MatchSetup | null>(null);
  useEffect(() => {
    const victory = sim.victory;
    const preset = matchSetup?.ruleset.preset;
    if (victory && victory.winner === localPlayerIndex && preset && isLadderId(preset)) {
      markMastered(preset);
    }
  }, [sim.victory, matchSetup, localPlayerIndex]);
  useEffect(() => {
    if (sim.status !== 'ready' || !matchSetup) {
      announcedFor.current = null;
      return;
    }
    if (announcedFor.current === matchSetup) return;
    announcedFor.current = matchSetup;
    const preset = matchSetup.ruleset.preset;
    const plan = newRulePlan(preset);
    if (!plan) return;
    const tick = Number(sim.tick ?? 0);
    const result = cinematics.director.offer(
      {
        milestoneId: 'graduation.new_rule',
        playerIndex: localPlayerIndex,
        tier: 'big',
        tick,
        data: { name: moduleLabel(t, plan.module) },
      },
      tick,
    );
    // A setting-filtered or not-for-me moment was not shown — don't burn
    // the module's place in the drip.
    if (result !== 'filtered' && result !== 'ignored') {
      markAnnounced(preset, plan.announced + 1);
    }
  }, [sim.status, matchSetup, sim.tick, cinematics.director, t, localPlayerIndex]);
}
